import { AutomateIcon } from '@eleveo/icons-react';
import { Icon, Tooltip } from '@zoom/components';
import classnames from 'classnames';
import React, { RefObject } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { Permissions, useAuth } from '../../../../app/AuthorizationProvider';
import {
  AGENTS_CONTEXT_MENU,
  CLEAR_AGENT_SCHEDULE_BUTTON_TAG,
  CLONE_AGENT_SCHEDULE_BUTTON_TAG,
  COPY_AGENT_SCHEDULE_BUTTON_TAG,
  DELETE_AGENT_BUTTON_TAG,
  PASTE_AGENT_SCHEDULE_BUTTON_TAG,
  SELECT_ALL_AGENT_SCHEDULE_BUTTON_TAG,
  TRIGGER_AGENT_AUTO_SCHEDULE_BUTTON_TAG,
} from '../../constants';
import { PinUnpinSubmenus } from '../PinUnpinSubmenus';

export interface AgentContextMenuProps extends WithTranslation {
  clearAgentSchedule?: () => void;
  cloneAgentSchedule?: () => void;
  copyAgentSchedule?: () => void;
  isAutoScheduleLimitExceeded: boolean;
  pasteAgentSchedule?: () => void;
  pinAllScheduleElements: () => void;
  pinElementsInCurrentDay: () => void;
  pinnableElementsCount: number;
  pinnableElementsInCurrentDayCount: number;
  pinnableSelectedElementsInCurrentDayCount: number;
  pinnedElementsCount: number;
  pinnedElementsInCurrentDayCount: number;
  pinnedSelectedElementsInCurrentDayCount: number;
  pinSelectedElements: () => void;
  removeAgentFromSchedule?: () => void;
  selectAllAgentSchedule?: () => void;
  shiftTemplateIds?: string[] | null;
  triggerCreateAutoScheduleJob?: () => void;
  unpinAllScheduleElements: () => void;
  unpinElementsInCurrentDay: () => void;
  unpinSelectedElements: () => void;
  wrapperRef: RefObject<HTMLDivElement>;
}

const AgentContextMenuComponent: React.FC<AgentContextMenuProps> = ({
  clearAgentSchedule,
  cloneAgentSchedule,
  copyAgentSchedule,
  isAutoScheduleLimitExceeded,
  pasteAgentSchedule,
  pinAllScheduleElements,
  pinElementsInCurrentDay,
  pinnableElementsCount,
  pinnableElementsInCurrentDayCount,
  pinnableSelectedElementsInCurrentDayCount,
  pinnedElementsCount,
  pinnedElementsInCurrentDayCount,
  pinnedSelectedElementsInCurrentDayCount,
  pinSelectedElements,
  removeAgentFromSchedule,
  selectAllAgentSchedule,
  shiftTemplateIds,
  t,
  triggerCreateAutoScheduleJob,
  unpinAllScheduleElements,
  unpinElementsInCurrentDay,
  unpinSelectedElements,
  wrapperRef,
}) => {
  const { userHasPermissions } = useAuth();
  const hasCreateAutoSchedulePermission = userHasPermissions([Permissions.CreateAutoScheduleJob]);

  const getAutoScheduleTooltipMessage = () => {
    let tooltipMessageKey = '';
    if (isAutoScheduleLimitExceeded) {
      tooltipMessageKey = 'schedule.autoSchedule.createDisabledLimitForUserOrRangeExceeded';
    } else if (!shiftTemplateIds?.length) {
      tooltipMessageKey = 'schedule.autoSchedule.createDisabledNoShiftTemplateForAgent';
    }

    return t(tooltipMessageKey);
  };

  const AutoScheduleButton = (
    <div
      data-test-id={TRIGGER_AGENT_AUTO_SCHEDULE_BUTTON_TAG}
      className={classnames('context-menu-item', {
        'context-menu-item--disabled': !triggerCreateAutoScheduleJob,
      })}
      onClick={triggerCreateAutoScheduleJob}
      style={{ paddingLeft: '11px' }}
    >
      <span className="context-menu-item-icon" style={{ marginRight: '12px' }}>
        <AutomateIcon className="text-xl text-chart-300 ml-0" />
      </span>
      <span className="context-menu-item-text">{t(`schedule.autoSchedule.title`)}</span>
    </div>
  );

  return (
    <div className="context-menu-wrapper" ref={wrapperRef}>
      {hasCreateAutoSchedulePermission && (
        <>
          {isAutoScheduleLimitExceeded || !shiftTemplateIds?.length ? (
            <Tooltip placement="bottom" message={getAutoScheduleTooltipMessage()}>
              {AutoScheduleButton}
            </Tooltip>
          ) : (
            AutoScheduleButton
          )}
        </>
      )}

      <div
        data-test-id={SELECT_ALL_AGENT_SCHEDULE_BUTTON_TAG}
        className={classnames('context-menu-item', {
          'context-menu-item--disabled': !selectAllAgentSchedule,
        })}
        onClick={selectAllAgentSchedule}
        style={{ paddingLeft: '11px' }}
      >
        <span className="context-menu-item-icon" style={{ marginRight: '9px' }}>
          <Icon kind="select_all_schedules" size={20} color="blue-light" />
        </span>
        <span className="context-menu-item-text">{t('layout.selectAll')}</span>
      </div>

      <div
        data-test-id={COPY_AGENT_SCHEDULE_BUTTON_TAG}
        className={classnames('context-menu-item', {
          'context-menu-item--disabled': !copyAgentSchedule,
        })}
        onClick={copyAgentSchedule}
      >
        <span className="context-menu-item-icon">
          <Icon kind="copy_schedule" size={16} color="blue-light" />
        </span>
        <span className="context-menu-item-text">{t('schedule.copy')}</span>
      </div>

      <div
        data-test-id={CLONE_AGENT_SCHEDULE_BUTTON_TAG}
        className={classnames('context-menu-item', {
          'context-menu-item--disabled': !cloneAgentSchedule,
        })}
        onClick={cloneAgentSchedule}
      >
        <span className="context-menu-item-icon">
          <Icon kind="duplicate_schedule" size={16} color="blue-light" />
        </span>
        <span className="context-menu-item-text">{t('schedule.clone')}</span>
      </div>

      <div
        data-test-id={PASTE_AGENT_SCHEDULE_BUTTON_TAG}
        className={classnames('context-menu-item', {
          'context-menu-item--disabled': !pasteAgentSchedule,
        })}
        onClick={pasteAgentSchedule}
      >
        <span className="context-menu-item-icon">
          <Icon kind="paste_schedule" size={16} color="green-lighter" />
        </span>
        <span className="context-menu-item-text">{t('schedule.paste')}</span>
      </div>

      <PinUnpinSubmenus
        className="context-menu-item"
        dataTestIdPrefix={AGENTS_CONTEXT_MENU}
        iconsClassName="mr-2"
        pinAllScheduleElements={pinAllScheduleElements}
        pinElementsInCurrentDay={pinElementsInCurrentDay}
        pinnableElementsCount={pinnableElementsCount}
        pinnableElementsInCurrentDayCount={pinnableElementsInCurrentDayCount}
        pinnableSelectedElementsInCurrentDayCount={pinnableSelectedElementsInCurrentDayCount}
        pinnedElementsCount={pinnedElementsCount}
        pinnedElementsInCurrentDayCount={pinnedElementsInCurrentDayCount}
        pinnedSelectedElementsInCurrentDayCount={pinnedSelectedElementsInCurrentDayCount}
        pinSelectedElements={pinSelectedElements}
        unpinAllScheduleElements={unpinAllScheduleElements}
        unpinElementsInCurrentDay={unpinElementsInCurrentDay}
        unpinSelectedElements={unpinSelectedElements}
      />

      <div
        data-test-id={CLEAR_AGENT_SCHEDULE_BUTTON_TAG}
        className={classnames('context-menu-item', {
          'context-menu-item--disabled': !clearAgentSchedule,
        })}
        onClick={clearAgentSchedule}
      >
        <span className="context-menu-item-icon">
          <Icon kind="cross" size={14} color="orange" />
        </span>
        <span className="context-menu-item-text">{t('schedule.clear')}</span>
      </div>

      <div
        data-test-id={DELETE_AGENT_BUTTON_TAG}
        className={classnames('context-menu-item', {
          'context-menu-item--disabled': !removeAgentFromSchedule,
        })}
        onClick={removeAgentFromSchedule}
      >
        <span className="context-menu-item-icon">
          <Icon kind="bin" size={15} color="red" />
        </span>
        <span className="context-menu-item-text">{t('person.removePerson')}</span>
      </div>
    </div>
  );
};

export const AgentContextMenu = withTranslation()(AgentContextMenuComponent);
